import { format } from "date-fns";

import {
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT
} from "constants/appointments";
import type { Schedule } from "services/types/schedules.type";

/** The event kinds that are `Schedule` rows, saved through `/schedules/`, not appointments. */
const SCHEDULE_KINDS = [
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT
];

export const isScheduleKind = (type: string) => SCHEDULE_KINDS.includes(type);

export interface ScheduleMoveInput {
  type: string;
  /** The `Schedule` row's own id. */
  id: number;
  startDate: Date;
  endDate: Date;
  /** The column the row was dropped on. */
  staffMemberId?: number;
  clinicId?: number;
  /** Clinic events only: the column it was dragged from, and its current people. */
  prevStaffMemberId?: number;
  staffMemberIds?: number[];
  isGlobalPracticeEvent?: boolean;
}

/**
 * The `PATCH /schedules/{id}/` body for a schedule row moved on the grid (ENG-2297).
 *
 * A row's kind is derived from `is_available`, `staff_member` and `appointment_types`
 * (`Schedule.type_prop`), so the body keeps each kind what it is:
 * - personal events, dedicated slots and work shifts carry their staff member, exactly as
 *   personal events and slots always did;
 * - a clinic event NEVER carries `staff_member`: a non-null one turns it into a personal
 *   event. Dropped on someone else's column it swaps that person in its `staff_members`;
 *   dropped on one of its own people's columns, or practice-wide, it moves in time only.
 */
export function scheduleMoveBody(move: ScheduleMoveInput): Partial<Schedule> {
  const when = {
    id: move.id,
    start_date: format(move.startDate, "yyyy-MM-dd"),
    end_date: format(move.endDate, "yyyy-MM-dd"),
    start_time: format(move.startDate, "HH:mm"),
    end_time: format(move.endDate, "HH:mm")
  };
  if (move.type !== EVENT_TYPE_CLINIC_EVENT)
    return {
      ...when,
      staff_member: move.staffMemberId,
      clinic: move.clinicId
    } as Partial<Schedule>;

  const to = move.staffMemberId;
  const from = move.prevStaffMemberId;
  const handsOver =
    !move.isGlobalPracticeEvent &&
    to != null &&
    from != null &&
    to !== from &&
    !(move.staffMemberIds ?? []).includes(to);
  return {
    ...when,
    clinic: move.clinicId,
    ...(handsOver ? { staff_members: { add: [to], remove: [from] } } : {})
  } as Partial<Schedule>;
}
